/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * feedApi
 * -------
 * Every request the athlete feed makes, as plain async functions that take an
 * `api` (an APIHelper, or a fake in tests) — the same shape checkInApi uses.
 *
 * Ownership is filtered explicitly. /arcee/application/table/fetch/ applies
 * row-level security, but a signed-in user with no group assignment reads at
 * GLOBAL scope (drf_permissions.py), which on a shared table means everyone's
 * rows. So every activity list query carries `created_by_id = <this user>` —
 * the application-user id the login response returns as `user_id`.
 *
 * The summary numbers (registrations badge, "Next up", the whole "Your
 * training" card) come from five saved analytics queries run in ONE
 * run-batch request. Those are scoped on the server with {{caller_filter}},
 * which binds the signed-in caller's id — nothing here can widen them.
 */

import envConfig from '../../config/envConfig';
import { parseStoredUtc } from '../../utils/zonedDateTime';

export const ACTIVITY_TABLE = 'activity';
export const GPS_TABLE = 'activity_gps_point';
export const SPLIT_TABLE = 'activity_split';
export const EVENT_TABLE = 'event';

/** Event statuses a visitor can still register for — the organizer page's "Live". */
export const LIVE_EVENT_STATUSES = ['PUBLISHED', 'REGISTRATION_OPEN'];

export const ACTIVITY_PAGE_SIZE = 20;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

/** One table/fetch call → { rows, count }. Throws on a non-OK response. */
export async function fetchRows(api, body, { signal, errorMessage } = {}) {
  const resp = await api.apiCall('/arcee/application/table/fetch/', {
    method: 'POST',
    signal,
    body: JSON.stringify({ ...scope(), ...body }),
  });
  if (!resp.ok) throw new Error(errorMessage || 'Could not load your data.');
  const payload = await resp.json();
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  const count = typeof payload?.count === 'number' ? payload.count : rows.length;
  return { rows, count };
}

/**
 * A foreign key's id, whichever way the server sent it. table/fetch expands
 * foreign keys as { related_table_name, record_identifier, data: { id, … } };
 * other paths send a bare id or { id }.
 */
export const idOf = (v) => {
  if (!v || typeof v !== 'object') return v;
  if (v.id !== undefined) return v.id;
  return v.data && typeof v.data === 'object' ? v.data.id : undefined;
};

const ownActivities = (userId) => ({ column_key: 'created_by_id', filter_type: 'equals', column_value: userId });

/** A page of the user's activities, newest first. `page` is 1-based. */
export function fetchActivityPage(api, { userId, page = 1, pageSize = ACTIVITY_PAGE_SIZE, signal }) {
  if (userId === null || userId === undefined) return Promise.resolve({ rows: [], count: 0 });
  return fetchRows(api, {
    table_name: ACTIVITY_TABLE,
    filters: [ownActivities(userId)],
    ordering: '-started_at',
    page,
    page_size: pageSize,
  }, { signal, errorMessage: 'Could not load your activities.' });
}

/** Analytics reads activities in pages this big until the server's count is reached. */
export const ANALYTICS_BATCH = 500;

/** "yyyy-MM-ddTHH:mm:ssZ" → the naive-UTC form the started_at column is compared in. */
const naiveUtc = (iso) => String(iso).replace(/Z$/, '');

/**
 * Every activity this user started in [fromIso, toIso) — the rows the Analytics
 * page aggregates. It is the same table/fetch call the UpVibe app syncs its
 * activity list with (SyncRepository.pullPage); the app then aggregates in
 * Room, the web aggregates in analyticsModel. The range is applied on the
 * server so a year of history is not pulled for a 30-day chart.
 */
export async function fetchActivitiesInRange(api, { userId, fromIso, toIso, signal }) {
  if (userId === null || userId === undefined) return [];
  const byId = new Map();
  for (let page = 1; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { rows, count } = await fetchRows(api, {
      table_name: ACTIVITY_TABLE,
      filters: [
        ownActivities(userId),
        { column_key: 'started_at', filter_type: 'greater_than_equal', column_value: naiveUtc(fromIso) },
        { column_key: 'started_at', filter_type: 'lesser_than', column_value: naiveUtc(toIso) },
      ],
      ordering: 'started_at',
      page,
      page_size: ANALYTICS_BATCH,
    }, { signal, errorMessage: 'Couldn’t load your analytics. Please try again.' });
    rows.forEach((r) => { if (r && r.id !== undefined && r.id !== null) byId.set(r.id, r); });
    if (rows.length < ANALYTICS_BATCH || page * ANALYTICS_BATCH >= count) return [...byId.values()];
  }
}

/**
 * One record by id through the single-record endpoint (table/fetch/<id>/) —
 * `id` is the primary key, not a registered field, so the list endpoint's
 * filters cannot select on it. Resolves to null on 404.
 */
export async function fetchRecord(api, table, recordId, { signal } = {}) {
  const resp = await api.apiCall(`/arcee/application/table/fetch/${encodeURIComponent(recordId)}/`, {
    method: 'POST',
    signal,
    body: JSON.stringify({ ...scope(), table_name: table }),
  });
  if (resp.status === 404 || resp.status === 403) return null;
  if (!resp.ok) throw new Error('Could not load this record.');
  return resp.json();
}

/** One activity, only if it belongs to this user (null otherwise). */
export async function fetchActivity(api, { userId, activityId, signal }) {
  if (userId === null || userId === undefined || !activityId) return null;
  const row = await fetchRecord(api, ACTIVITY_TABLE, activityId, { signal });
  if (!row || String(idOf(row.created_by_id)) !== String(userId)) return null;
  return row;
}

/** The route of one activity, in recording order. */
export async function fetchRoutePoints(api, { activityId, signal }) {
  const { rows } = await fetchRows(api, {
    table_name: GPS_TABLE,
    filters: [{ column_key: 'activity_id', filter_type: 'equals', column_value: activityId }],
    ordering: 'sequence',
    page: 1,
    page_size: 20000,
  }, { signal, errorMessage: 'Could not load the route.' });
  return rows;
}

export async function fetchSplits(api, { activityId, signal }) {
  const { rows } = await fetchRows(api, {
    table_name: SPLIT_TABLE,
    filters: [{ column_key: 'activity_id', filter_type: 'equals', column_value: activityId }],
    ordering: 'split_number',
    page: 1,
    page_size: 500,
  }, { signal, errorMessage: 'Could not load the splits.' });
  return rows;
}

/** The public list's largest page (MAX_PUBLIC_PAGE_SIZE on the server). */
const PUBLIC_PAGE_SIZE = 100;
/** Stop after this many pages; the card shows a handful, not the catalogue. */
const SUGGESTED_MAX_PAGES = 5;

/**
 * Live (published or open for registration) events not started yet, soonest first.
 *
 * Read from the PUBLIC event list (the one "See all" → /p/event uses), not
 * table/fetch. On table/fetch an organizer's SELF group scope applies to
 * `event`, so a signed-in organizer browsing as an athlete saw only their own
 * events here. The public list carries no group scope, but its view has
 * filtering and sorting switched off, so status, start time and order are
 * applied here instead.
 *
 * `fetchPage(params)` returns one public list page:
 * { records, pagination: { total_pages } } — fetchPublicTableList bound to the
 * event table.
 */
export async function fetchSuggestedEvents(fetchPage, { now = new Date(), limit = 4 } = {}) {
  const rows = [];
  for (let page = 1; page <= SUGGESTED_MAX_PAGES; page += 1) {
    const body = await fetchPage({ page, page_size: PUBLIC_PAGE_SIZE });
    rows.push(...(body?.records || []));
    if (page >= (body?.pagination?.total_pages || 1)) break;
  }
  const from = now.getTime();
  return rows
    .filter((e) => LIVE_EVENT_STATUSES.includes(e.status) && eventStart(e) > from && eventStart(e) !== Infinity)
    .sort((a, b) => eventStart(a) - eventStart(b))
    .slice(0, limit);
}

/** Event times are stored as naive UTC — read them as UTC, not browser-local. */
function toTime(value) {
  const d = value ? parseStoredUtc(value) : null;
  return d ? d.getTime() : null;
}

export function eventStart(event) {
  return toTime(event?.start_datetime) ?? Infinity;
}

export function eventEnd(event) {
  return toTime(event?.end_datetime) ?? toTime(event?.start_datetime) ?? Infinity;
}

/* ─────────────────────────── Athlete summary (one request) ─────────────────────────── */

/**
 * Saved queries seeded by arcee's `seed_analytics_queries` command. All five
 * run in one POST to /analytics/run-batch/; the ones that bucket by day or week
 * take the viewer's IANA zone so a week starts on the athlete's own Monday.
 */
export const SUMMARY_QUERIES = [
  'athlete_registration_summary',
  'athlete_upcoming_events',
  'athlete_weekly_training',
  'athlete_active_days',
  'athlete_best_efforts',
];
const ZONED = new Set(['athlete_weekly_training', 'athlete_active_days', 'athlete_best_efforts']);

export function viewerTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
}

export async function fetchAthleteSummary(api, { tz = viewerTimeZone(), signal } = {}) {
  const resp = await api.apiCall(`/arcee/application/${Number(envConfig.applicationId)}/analytics/run-batch/`, {
    method: 'POST',
    signal,
    body: JSON.stringify({
      queries: SUMMARY_QUERIES.map((name) => ({ name, params: ZONED.has(name) ? { tz } : {} })),
    }),
  });
  if (!resp.ok) throw new Error('Could not load your training summary.');
  const payload = await resp.json();
  return parseAthleteSummary(payload?.results || {});
}

/** One run-batch result → row objects keyed by column name, or null if it failed. */
export function recordsOf(result) {
  if (!result || result.status !== 'ok') return null;
  const names = (result.columns || []).map((c) => (c && typeof c === 'object' ? c.name : c));
  return (result.rows || []).map((row) => Object.fromEntries(names.map((name, i) => [name, row[i]])));
}

/** Numbers arrive as JSON numbers or, for NUMERIC, as strings. */
const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? 0 : Number(v));

/** 'YYYY-MM-DD' → that calendar day at local midnight (the query already used the viewer's zone). */
function localDay(value) {
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

const BEST_LABELS = [
  ['fastest_5k', 'Fastest 5K'],
  ['fastest_10k', 'Fastest 10K'],
  ['longest_run', 'Longest run'],
  ['longest_ride', 'Longest ride'],
];

/**
 * The batch response → the shape the feed reads. A query that failed on the
 * server leaves its part empty/null rather than failing the rest.
 */
export function parseAthleteSummary(results) {
  const reg = recordsOf(results.athlete_registration_summary);
  const upcoming = recordsOf(results.athlete_upcoming_events);
  const weeks = recordsOf(results.athlete_weekly_training);
  const days = recordsOf(results.athlete_active_days);
  const best = recordsOf(results.athlete_best_efforts);

  const bestMap = new Map();
  if (best && best[0]) {
    BEST_LABELS.forEach(([column, label]) => {
      const id = best[0][column];
      if (id !== null && id !== undefined && !bestMap.has(id)) bestMap.set(id, label);
    });
  }
  return {
    registrations: reg && reg[0] ? { total: num(reg[0].events_registered), upcoming: num(reg[0].upcoming_events) } : null,
    upcomingEvents: (upcoming || []).map((e) => ({
      id: e.event_id, title: e.title, start_datetime: e.start_datetime,
      end_datetime: e.end_datetime, location: e.location, status: e.status,
    })),
    weeks: (weeks || []).map((w) => ({
      start: localDay(w.week_start), current: Boolean(w.is_current),
      km: num(w.km), sec: num(w.moving_seconds), kcal: num(w.kcal), count: num(w.activities),
    })),
    avgKm: weeks && weeks[0] ? num(weeks[0].avg_km_12w) : 0,
    avgSec: weeks && weeks[0] ? num(weeks[0].avg_moving_seconds_12w) : 0,
    activeDays: (days || []).map((d) => String(d.day).slice(0, 10)),
    best: bestMap,
  };
}

/* ─────────────────────────── New-activity check (polled) ─────────────────────────── */

/**
 * Count and newest write of the caller's activities — one row from the saved
 * query `athlete_activity_changes` (scoped on the server with {{caller_filter}}).
 * A different fingerprint from the last poll means something was synced.
 */
export async function fetchActivityChanges(api, { signal } = {}) {
  const resp = await api.apiCall(
    `/arcee/application/${Number(envConfig.applicationId)}/analytics/athlete_activity_changes/run/`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error('Could not check for new activities.');
  const [row] = recordsOf({ status: 'ok', ...(await resp.json()) }) || [];
  const count = num(row?.cnt);
  const latest = row?.latest ?? null;
  return { count, latest, fingerprint: `${count}.${latest ?? ''}` };
}
